import React, { useCallback } from "react";
import SearchResultsWrapperDesktop from "./SearchResultsWrapper.desktop.js";
import { HiddenVisually, Stack, useViewport } from "@bookingcom/bui-react";
import SearchResultBanner from "../SearchResultBanner/SearchResultBanner.js";
import useSearchResultsDesktopController from "./useSearchResults.desktop.controller.js";
import SkeletonSearchDesktop, { SkeletonSearchContent } from "../Skeleton/SkeletonPages/SkeletonSearch.desktop.js";
import FailedSearchResults from "./FailedSearchResults.desktop.js";
import { RemoveDirectFlights } from "./components/RemoveDirectFlights.js";
import { TooManyFilters } from "./components/TooManyFilters.js";
import { NoResultsFound } from "./components/NoResultsFound.js";
import SearchResultsContentDesktop from "./SearchResultsContent.desktop.js";
import useCreateUrl from "../../../hooks/useCreateUrl.js";
import { useHistory, useParams } from "react-router-dom";
import { getLocationParams, getVisibleFlights } from "./utils.js";
import SearchResultsListInboundDesktop from "./SearchResultsList.inbound.desktop.js";
import useOfferCartOrderData from "../../../hooks/useOfferCartOrderData.js";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { DestinationUkraineWarning } from "../TravelAlert/index.js";
import SirfInfoBanner from "../SirfInfoBanner/index.js";
import SearchBaggagePolicies from "../SearchBaggagePolicies/index.js";
import PriceAlertToggle from "../PriceAlerts/PriceAlertToggle.js";
import { useIsPriceAlertsEligible } from "../PriceAlerts/usePriceAlertToggle.js";
import BackToOutbound from "./components/BackToOutbound.js";
import Frame from "../Frame/index.js";
import SearchSortSelector from "./components/SearchSortSelector.js";
import { useTrackSplitBoundsInboundEmptyError } from "../../../utils/experiments/funnel/flights_split_bounds_us.js";
const Header = () => {
    const { isLarge } = useViewport();
    return (React.createElement(Frame, { pb: 4 },
        React.createElement(Stack, { direction: !isLarge ? "column" : "row", gap: !isLarge ? 4 : 2, alignItems: !isLarge ? "start" : "center" },
            React.createElement(Stack.Item, { grow: true },
                React.createElement(BackToOutbound, null)),
            React.createElement(SearchSortSelector, null))));
};
const SearchResultsInboundDesktop = ({ onSelectFlight, outboundBrandedFareName }) => {
    const { getScreenReaderAnnouncementText, appliedFilters, isFirstLoad, canExtendSearch, onExpandToCitySearch, canRemoveDirectFlightsFilter, flights, fetchStatus, banners, isSirfAvailable, fetchedAt, searchCriteria } = useSearchResultsDesktopController();
    const i18n = useI18n();
    const isPriceAlertsEligible = useIsPriceAlertsEligible();
    const visibleFlights = getVisibleFlights(flights);
    const hasOnlyHiddenFlights = flights.length > 0 && visibleFlights.length === 0;
    useTrackSplitBoundsInboundEmptyError({
        fetchStatus,
        flightsCount: flights.length,
        visibleFlightsCount: visibleFlights.length
    });
    const humanRightBanner = banners?.find((banner) => banner.id === "LEGAL_NOTICE_HUMAN_RIGHTS");
    const { createUrl } = useCreateUrl();
    const history = useHistory();
    const { airports, outboundToken } = useParams();
    const offerCartOrderData = useOfferCartOrderData();
    const onClickSelectFlight = useCallback(async (flight) => {
        onSelectFlight?.(flight);
        offerCartOrderData.set(flight.token, {
            offer: flight.token,
            cabinClass: searchCriteria?.cabinClass
        });
        const url = createUrl(`/flights/${airports}/inbound/${outboundToken}/${flight.token}/?${getLocationParams(location.search)}`);
        history.push(url);
    }, [onSelectFlight, offerCartOrderData, searchCriteria?.cabinClass, createUrl, airports, outboundToken, history]);
    let resultingComp = undefined;
    if (fetchStatus === "initial" || fetchStatus === "loading") {
        if (isFirstLoad) {
            return React.createElement(SkeletonSearchDesktop, null);
        }
        resultingComp = React.createElement(SkeletonSearchContent, null);
    }
    if (fetchStatus === "failed" && canExtendSearch) {
        return React.createElement(FailedSearchResults, { onClickExpandToCitySearch: onExpandToCitySearch });
    }
    if (fetchStatus === "success") {
        if (visibleFlights.length > 0) {
            resultingComp = (React.createElement(SearchResultsContentDesktop, { fetchedAt: fetchedAt },
                React.createElement(DestinationUkraineWarning, null),
                isSirfAvailable && (React.createElement(SirfInfoBanner, { bannerName: "search-results", bannerContent: i18n.trans(t("flights_sr_spanish_discount_banner")) })),
                React.createElement(SearchBaggagePolicies, null),
                isPriceAlertsEligible && React.createElement(PriceAlertToggle, null),
                React.createElement(SearchResultsListInboundDesktop, { flights: visibleFlights, onSelectFlight: onClickSelectFlight, outboundBrandedFareName: outboundBrandedFareName })));
        }
        else if (hasOnlyHiddenFlights) {
        }
        else if (canRemoveDirectFlightsFilter) {
            return (React.createElement(React.Fragment, null,
                React.createElement(SearchResultBanner, { banner: humanRightBanner }),
                React.createElement(RemoveDirectFlights, null)));
        }
    }
    if (!resultingComp) {
        if (appliedFilters.length > 0) {
            resultingComp = React.createElement(TooManyFilters, null);
        }
        else {
            return React.createElement(NoResultsFound, null);
        }
    }
    return (React.createElement(SearchResultsWrapperDesktop, { banners: React.createElement(React.Fragment, null,
            humanRightBanner && React.createElement(SearchResultBanner, { banner: humanRightBanner }),
            React.createElement(Header, null)), showGenAiSummary: false },
        resultingComp,
        React.createElement(HiddenVisually, null,
            React.createElement("div", { "aria-live": "polite" }, getScreenReaderAnnouncementText()))));
};
export default React.memo(SearchResultsInboundDesktop);
